/* eslint-disable @typescript-eslint/no-empty-object-type */
/* eslint-disable react/no-unknown-property */

"use client";

import { type DialogProps } from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";

import { css, sva } from "@/../styled-system/css";
import Search from "@/assets/svg/search.svg";

import { Dialog, DialogContent } from "./Dialog";

const commandSVA = sva({
  slots: [
    "root",
    "inputWrapper",
    "inputSearch",
    "input",
    "list",
    "empty",
    "group",
    "separator",
    "item",
    "shortcut",
  ],
  base: {
    root: {
      display: "flex",
      h: "full",
      w: "full",
      flexDirection: "column",
      overflow: "hidden",
      rounded: "md",
      bg: "white",
      color: "black",
    },
    inputWrapper: {
      display: "flex",
      alignItems: "center",
      borderBottomWidth: "1px",
      borderColor: "zinc.200",
      px: "3",
    },
    inputSearch: {
      marginRight: "2",
      flexShrink: "0",
      opacity: "50",
    },
    input: {
      display: "flex",
      h: "44px",
      w: "full",
      rounded: "md",
      bg: "transparent",
      py: "3",
      textStyle: "sm",
      outline: "none",
      boxShadow: "none",
      _placeholder: {
        color: "gray.500",
      },
      _disabled: {
        cursor: "not-allowed",
        opacity: "0.5",
      },
    },
    list: {
      maxH: "300px",
      overflowY: "auto",
      overflowX: "hidden",
    },
    empty: {
      py: "6",
      textAlign: "center",
      textStyle: "sm",
    },
    group: {
      overflow: "hidden",
      p: "1",
      color: "black",

      "& [cmdk-group-heading]": {
        px: "2",
        py: "1.5",
        textStyle: "xs",
        fontWeight: "medium",
        color: "muted.foreground",
      },
    },
    separator: {
      mx: "-1",
      h: "1px",
      bg: "zinc.200",
    },
    item: {
      position: "relative",
      display: "flex",
      cursor: "default",
      userSelect: "none",
      alignItems: "center",
      rounded: "sm",
      px: "2",
      py: "1.5",
      textStyle: "sm",
      outline: "none",
      gap: "2",

      "&[aria-selected=true]": {
        backgroundColor: "brandBlue/15",
      },

      "&[data-disabled=true]": {
        opacity: "0.5",
      },
    },
    shortcut: {
      ml: "auto",
      textStyle: "xs",
      //   tracking: "widest",
      color: "black",
    },
  },
});

const commandDialog = sva({
  slots: ["content", "command"],
  base: {
    content: {
      overflow: "hidden",
      p: "0 !important",
      boxShadow: "lg !important",
    },
    command: {
      "& [cmdk-group-heading]": {
        px: "2",
        fontWeight: "medium",
        color: "black",
      },
      "& [cmdk-group]:not([hidden]) ~[cmdk-group]": {
        pt: 0,
      },
      "& [cmdk-group]": {
        px: "2",
      },
      "& [cmdk-input-wrapper] svg": {
        h: "5",
        w: "5",
      },
      "& [cmdk-input]": {
        h: "12",
      },
      "& [cmdk-item]": {
        py: "3",
      },
      "& [cmdk-item] svg": {
        h: "5",
        w: "5",
      },
    },
  },
});

const {
  root,
  inputWrapper,
  inputSearch,
  input,
  list,
  empty,
  group,
  separator,
  item,
  shortcut,
} = commandSVA.raw();

const { content, command } = commandDialog.raw();

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const classsName = css(root, cssProp);

  return <CommandPrimitive ref={ref} className={classsName} {...props} />;
});
Command.displayName = CommandPrimitive.displayName;

interface CommandDialogProps extends DialogProps {}

const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent css={content}>
        <Command css={command}>{children}</Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(input, cssProp);

  return (
    <div className={css(inputWrapper)} cmdk-input-wrapper="">
      <Search width={20} stroke="black" className={css(inputSearch)} />
      <CommandPrimitive.Input ref={ref} className={className} {...props} />
    </div>
  );
});
CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(list, cssProp);

  return <CommandPrimitive.List ref={ref} className={className} {...props} />;
});
CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(empty, cssProp);

  return <CommandPrimitive.Empty ref={ref} className={className} {...props} />;
});
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(group, cssProp);

  return <CommandPrimitive.Group ref={ref} className={className} {...props} />;
});
CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(separator, cssProp);

  return (
    <CommandPrimitive.Separator ref={ref} className={className} {...props} />
  );
});
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(item, cssProp);

  return <CommandPrimitive.Item ref={ref} className={className} {...props} />;
});
CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
  css: cssProp = {},
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { css?: object }) => {
  const className = css(shortcut, cssProp);

  return <span className={className} {...props} />;
};
CommandShortcut.displayName = "CommandShortcut";

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
